import { Wordle } from '@minigame-race/contracts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { MinigameProps } from '../../race/types.js';
import { color } from '../../theme.js';

/**
 * Wordle.
 *
 * No optimistic prediction here, deliberately: a guess's feedback colours cannot
 * be computed client-side without knowing the answer, and the answer is exactly
 * what the server refuses to send. So the current row is local (typing is
 * instant) and everything above it is authoritative.
 */

const ROWS: readonly string[][] = [
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
  ['ENTER', 'z', 'x', 'c', 'v', 'b', 'n', 'm', 'BACK'],
];

function isWordleView(v: unknown): v is Wordle.WordlePrivateView {
  return typeof v === 'object' && v !== null && (v as { kind?: string }).kind === 'wordle';
}

/** Best-known status per letter, for keyboard tinting. correct > present > absent. */
function keyboardState(guesses: readonly Wordle.ScoredGuess[]): Map<string, Wordle.LetterResult> {
  const rank: Record<Wordle.LetterResult, number> = { absent: 0, present: 1, correct: 2 };
  const map = new Map<string, Wordle.LetterResult>();
  for (const g of guesses) {
    for (let i = 0; i < g.word.length; i++) {
      const ch = g.word[i];
      const r = g.result[i];
      if (ch === undefined || r === undefined) continue;
      const prev = map.get(ch);
      if (prev === undefined || rank[r] > rank[prev]) map.set(ch, r);
    }
  }
  return map;
}

type TileState = Wordle.LetterResult | 'empty' | 'typing' | 'hint';

const tileColors = (r: TileState) => {
  switch (r) {
    case 'correct':
      // Right letter, right spot. Lime — the same "you" colour as the whole HUD.
      return { background: `linear-gradient(160deg,${color.limeBright},${color.limeDeep})`, color: color.limeInk, border: 'none' };
    case 'present':
      // Right letter, wrong spot. Amber.
      return { background: 'linear-gradient(160deg,#ffc75a,#f0a000)', color: color.amberInk, border: 'none' };
    case 'absent':
      return { background: 'rgba(255,255,255,.045)', color: 'rgba(234,255,234,.35)', border: 'none' };
    case 'typing':
      return { background: 'rgba(166,255,46,.05)', color: color.text, border: `clamp(2px,.5cqmin,3px) solid rgba(166,255,46,.6)` };
    case 'hint':
      // A letter you bought. Ghosted lime outline: it IS a green, but you paid
      // for it, so it must not look identical to one you deduced.
      return {
        background: 'rgba(166,255,46,.10)',
        color: 'rgba(198,255,94,.75)',
        border: `clamp(2px,.5cqmin,3px) dashed rgba(166,255,46,.55)`,
      };
    default:
      return { background: 'transparent', color: color.text, border: '2px solid rgba(234,255,234,.1)' };
  }
};

export function WordleGame({ view, emit, rejectNonce }: MinigameProps) {
  const server = isWordleView(view) ? view : null;
  const [draft, setDraft] = useState('');
  const [shake, setShake] = useState(0);

  /**
   * Letters in THIS puzzle. Seeded per segment, so it comes off the wire — never
   * a hardcoded 5. Falls back to the max while the first state-view is in
   * flight, which only affects the empty grid we render for a few milliseconds.
   */
  const wordLength: Wordle.WordLength = server?.wordLength ?? Wordle.MAX_WORD_LENGTH;

  // A rejected guess (not a word, already tried) shakes the row and clears it.
  //
  // Only clear when the draft was a complete word — that is the only case where
  // the rejection can be about the draft. A refused hint must not wipe letters
  // the player has already typed.
  useEffect(() => {
    if (rejectNonce === 0) return;
    setShake((s) => s + 1);
    setDraft((d) => (d.length === wordLength ? '' : d));
  }, [rejectNonce, wordLength]);

  // Server accepted a guess: the row committed, so clear the draft.
  const guessCount = server?.guessesUsed ?? 0;
  useEffect(() => {
    setDraft('');
  }, [guessCount]);

  // A new segment can have a different length. Drop anything half-typed rather
  // than carry a 5-letter draft into a 4-letter puzzle.
  useEffect(() => {
    setDraft('');
  }, [wordLength]);

  const submit = useCallback(() => {
    if (draft.length !== wordLength) {
      setShake((s) => s + 1);
      return;
    }
    emit({ type: 'guess', word: draft } satisfies Wordle.WordleGuessEvent);
  }, [draft, emit, wordLength]);

  const press = useCallback(
    (key: string) => {
      if (server?.isComplete) return;
      if (key === 'ENTER') submit();
      else if (key === 'BACK') setDraft((d) => d.slice(0, -1));
      else if (/^[a-z]$/.test(key)) setDraft((d) => (d.length < wordLength ? d + key : d));
    },
    [server?.isComplete, submit, wordLength],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Enter') press('ENTER');
      else if (e.key === 'Backspace') press('BACK');
      else if (/^[a-zA-Z]$/.test(e.key)) press(e.key.toLowerCase());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [press]);

  const kbState = useMemo(() => keyboardState(server?.guesses ?? []), [server?.guesses]);

  if (!server) {
    return <div style={{ color: color.textFaint, fontSize: 14 }}>Loading puzzle…</div>;
  }

  /** index -> revealed letter, for cells the player has paid to see. */
  const hintAt = new Map(server.hints.map((h) => [h.index, h.letter]));

  const rows = Array.from({ length: Wordle.MAX_GUESSES }, (_, r) => {
    const done = server.guesses[r];
    if (done) return { letters: [...done.word], results: [...done.result] as TileState[] };

    if (r === server.guessesUsed && !server.isComplete) {
      // The row being typed. Bought letters show through wherever the player
      // hasn't typed over them yet, so a hint stays visible while you work.
      const typed = draft.padEnd(wordLength, ' ').split('');
      const letters = typed.map((c, i) => (c.trim() ? c : (hintAt.get(i) ?? ' ')));
      const results = typed.map((c, i): TileState => {
        if (c.trim()) return 'typing';
        if (hintAt.has(i)) return 'hint';
        return 'empty';
      });
      return { letters, results, current: true };
    }

    return {
      letters: Array<string>(wordLength).fill(' '),
      results: Array<TileState>(wordLength).fill('empty'),
    };
  });

  return (
    <div style={{ width: 'min(64cqh,92cqw)', display: 'flex', flexDirection: 'column', gap: 'clamp(8px,1.8cqmin,18px)' }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 'clamp(5px,1cqmin,9px)' }}>
            <span style={{ fontWeight: 700, fontSize: 'clamp(15px,3cqmin,27px)', letterSpacing: '.04em' }}>WORDLE</span>
            {/* The length changes per segment. Say it, or a player types five
                letters into a four-letter grid and wonders why ENTER does nothing. */}
            <span
              style={{
                fontFamily: "'JetBrains Mono',monospace",
                fontWeight: 800,
                fontSize: 'clamp(10px,2cqmin,16px)',
                padding: '2px 7px',
                borderRadius: 5,
                background: 'rgba(166,255,46,.14)',
                color: color.limeBright,
                letterSpacing: '.04em',
                whiteSpace: 'nowrap',
              }}
            >
              {wordLength} LETTERS
            </span>
          </div>
          <span
            title={Wordle.LANGUAGE_HINT}
            style={{
              fontSize: 'clamp(7px,1.3cqmin,11px)',
              letterSpacing: '.24em',
              fontWeight: 600,
              color: 'rgba(166,255,46,.6)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {Wordle.LANGUAGE_NOTE}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(6px,1.2cqmin,10px)' }}>
          {/* Buy a hint. Priced so it's a bargain when you're about to eat the
              60s failed-solve penalty and a ripoff when you're cruising. */}
          {server.hintsRemaining > 0 && !server.isComplete && (
            <button
              onPointerDown={(e) => {
                e.preventDefault();
                emit({ type: 'hint' } satisfies Wordle.WordleHintEvent);
              }}
              title="Reveals one letter and its position"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'clamp(4px,.9cqmin,7px)',
                padding: 'clamp(4px,.9cqmin,8px) clamp(9px,1.8cqmin,15px)',
                border: '1px dashed rgba(255,176,32,.55)',
                borderRadius: 999,
                background: 'rgba(255,176,32,.08)',
                touchAction: 'manipulation',
              }}
            >
              <span style={{ fontSize: 'clamp(7px,1.3cqmin,11px)', letterSpacing: '.16em', fontWeight: 700, color: color.amberBright }}>
                💡 PODPOWIEDŹ
              </span>
              <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 800, fontSize: 'clamp(10px,2.1cqmin,17px)', lineHeight: 1, color: color.amber }}>
                +{Math.round(server.hintPenaltyMs / 1000)}s
              </span>
            </button>
          )}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'clamp(5px,1.1cqmin,9px)',
              padding: 'clamp(4px,.9cqmin,8px) clamp(9px,1.8cqmin,16px)',
              border: '1px solid rgba(166,255,46,.4)',
              borderRadius: 999,
              background: 'rgba(166,255,46,.08)',
            }}
          >
            <span style={{ fontSize: 'clamp(7px,1.3cqmin,11px)', letterSpacing: '.2em', fontWeight: 600, color: 'rgba(166,255,46,.75)' }}>
              GUESS
            </span>
            <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 800, fontSize: 'clamp(14px,3cqmin,24px)', lineHeight: 1, color: color.limeBright }}>
              {server.guessesUsed}
              <span style={{ color: 'rgba(234,255,234,.4)', fontWeight: 600 }}>/{server.maxGuesses}</span>
            </span>
          </div>
        </div>
      </div>

      {/* grid */}
      {/* Width scales with the number of columns so a 4-letter tile is the same
          size as a 5-letter one, rather than 25% fatter. */}
      <div
        style={{
          width: `calc(min(38cqh,54cqw) * ${wordLength / Wordle.MAX_WORD_LENGTH})`,
          alignSelf: 'center',
          display: 'grid',
          gridTemplateRows: `repeat(${Wordle.MAX_GUESSES},1fr)`,
          gap: 'clamp(4px,1cqmin,9px)',
          aspectRatio: `${wordLength} / ${Wordle.MAX_GUESSES}`,
        }}
      >
        {rows.map((row, r) => (
          <div
            key={r}
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${wordLength},1fr)`,
              gap: 'clamp(4px,1cqmin,9px)',
              animation: row.current && shake > 0 ? `shake .3s ease-in-out ${shake}` : undefined,
            }}
          >
            {row.letters.map((ch, c) => {
              const res = row.results[c] ?? 'empty';
              const st = tileColors(res);
              const atCursor = row.current === true && c === draft.length && draft.length < wordLength;
              // A bought letter stays legible under the cursor: we mark the cell
              // with the pulsing border rather than replacing its glyph with a
              // caret. Otherwise the hint you just paid 10s for is invisible.
              const showCaret = atCursor && res !== 'hint';
              return (
                <div
                  key={c}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 'clamp(3px,.7cqmin,6px)',
                    fontWeight: 700,
                    fontSize: 'clamp(15px,3.4cqmin,30px)',
                    textTransform: 'uppercase',
                    boxShadow: res === 'correct' ? '0 0 clamp(6px,1.6cqmin,16px) rgba(166,255,46,.4)' : undefined,
                    animation: atCursor ? 'whCur 1s ease-in-out infinite' : undefined,
                    ...st,
                  }}
                >
                  {showCaret ? (
                    <span style={{ width: 'clamp(2px,.5cqmin,3px)', height: 'clamp(15px,3cqmin,26px)', background: color.lime, animation: 'caret 1s steps(1,end) infinite' }} />
                  ) : (
                    ch.trim()
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* keyboard */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(3px,.8cqmin,7px)' }}>
        {ROWS.map((row, i) => (
          <div key={i} style={{ display: 'flex', gap: 'clamp(3px,.7cqmin,6px)', padding: i === 1 ? '0 clamp(10px,3cqmin,30px)' : undefined }}>
            {row.map((key) => {
              const wide = key.length > 1;
              const st = kbState.get(key);
              const bg =
                st === 'correct'
                  ? `linear-gradient(160deg,${color.limeBright},${color.limeDeep})`
                  : st === 'present'
                    ? 'linear-gradient(160deg,#ffc75a,#f0a000)'
                    : st === 'absent'
                      ? 'rgba(255,255,255,.03)'
                      : key === 'ENTER'
                        ? 'rgba(166,255,46,.16)'
                        : 'rgba(255,255,255,.07)';
              const fg =
                st === 'correct' ? color.limeInk : st === 'present' ? color.amberInk : st === 'absent' ? 'rgba(234,255,234,.3)' : key === 'ENTER' ? color.limeBright : color.text;
              return (
                <button
                  key={key}
                  onPointerDown={(e) => {
                    e.preventDefault();
                    press(key);
                  }}
                  style={{
                    flex: wide ? 1.6 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 'clamp(30px,5.4cqmin,46px)',
                    borderRadius: 'clamp(3px,.9cqmin,7px)',
                    fontWeight: 700,
                    fontSize: wide ? 'clamp(9px,1.7cqmin,14px)' : 'clamp(11px,2cqmin,17px)',
                    letterSpacing: wide ? '.06em' : undefined,
                    background: bg,
                    color: fg,
                    textTransform: 'uppercase',
                    touchAction: 'manipulation',
                  }}
                >
                  {key === 'BACK' ? '⌫' : key}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Why a guess bounced. Without this, a rejected `zolty` looks like a bug. */}
      {shake > 0 && !server.isComplete && (
        <div
          style={{
            textAlign: 'center',
            fontSize: 'clamp(9px,1.6cqmin,13px)',
            color: 'rgba(255,176,32,.85)',
            fontWeight: 600,
            letterSpacing: '.04em',
          }}
        >
          {Wordle.LANGUAGE_HINT}
        </div>
      )}

      {/* Answer is only ever present after the segment closes. */}
      {server.isComplete && !server.solved && server.answer && (
        <div style={{ textAlign: 'center', fontSize: 'clamp(11px,2cqmin,16px)', color: color.redBright, fontWeight: 700, letterSpacing: '.06em' }}>
          ANSWER WAS <span style={{ color: color.text }}>{server.answer.toUpperCase()}</span> · +60s
        </div>
      )}
    </div>
  );
}
